import React, { useEffect, useState } from 'react';
import { Client } from '../../data/clients';
import { X } from 'lucide-react';
interface ClientFormProps {
  client: Client | null;
  onSave: (client: Client) => void;
  onClose: () => void;
}
export function ClientForm({
  client,
  onSave,
  onClose
}: ClientFormProps) {
  const [formData, setFormData] = useState<Partial<Client>>({
    name: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      region: 'Capitale-Nationale',
      coordinates: {
        lat: 46.8139,
        lng: -71.208
      }
    },
    contractType: 'forfait',
    priority: 'medium',
    services: [],
    contact: {
      name: '',
      phone: '',
      email: ''
    },
    snowRemovalDuration: 60
  });
  useEffect(() => {
    if (client) {
      setFormData(client);
    }
  }, [client]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newClient: Client = {
      id: client?.id || `CLT-${Date.now()}`,
      name: formData.name!,
      address: formData.address!,
      contractType: formData.contractType!,
      priority: formData.priority!,
      services: formData.services!,
      contact: formData.contact!,
      snowRemovalDuration: formData.snowRemovalDuration!
    };
    onSave(newClient);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-33">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-34">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-35">
          <h2 className="text-xl font-semibold text-gray-900" data-id="element-36">
            {client ? 'Modifier le client' : 'Nouveau client'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-37">
            <X className="w-5 h-5 text-gray-500" data-id="element-38" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-39">
          {/* Basic Info */}
          <div data-id="element-40">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-41">
              Informations générales
            </h3>
            <div className="space-y-4" data-id="element-42">
              <div data-id="element-43">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-44">
                  Nom du client *
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-45" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-46">
                <div data-id="element-47">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-48">
                    Type de contrat *
                  </label>
                  <select value={formData.contractType} onChange={e => setFormData({
                  ...formData,
                  contractType: e.target.value as Client['contractType']
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-49">
                    <option value="forfait" data-id="element-50">Forfait</option>
                    <option value="remorque" data-id="element-51">Remorque</option>
                    <option value="prestation" data-id="element-52">Prestation</option>
                  </select>
                </div>
                <div data-id="element-53">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-54">
                    Priorité *
                  </label>
                  <select value={formData.priority} onChange={e => setFormData({
                  ...formData,
                  priority: e.target.value as Client['priority']
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-55">
                    <option value="high" data-id="element-56">Haute</option>
                    <option value="medium" data-id="element-57">Moyenne</option>
                    <option value="low" data-id="element-58">Basse</option>
                  </select>
                </div>
              </div>
              <div data-id="element-59">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-60">
                  Durée déneigement (minutes) *
                </label>
                <input type="number" required min="15" step="15" value={formData.snowRemovalDuration} onChange={e => setFormData({
                ...formData,
                snowRemovalDuration: parseInt(e.target.value)
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-61" />
              </div>
            </div>
          </div>
          {/* Address */}
          <div data-id="element-62">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-63">
              Adresse
            </h3>
            <div className="space-y-4" data-id="element-64">
              <div data-id="element-65">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-66">
                  Rue *
                </label>
                <input type="text" required value={formData.address?.street} onChange={e => setFormData({
                ...formData,
                address: {
                  ...formData.address!,
                  street: e.target.value
                }
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-67" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-68">
                <div data-id="element-69">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-70">
                    Ville *
                  </label>
                  <input type="text" required value={formData.address?.city} onChange={e => setFormData({
                  ...formData,
                  address: {
                    ...formData.address!,
                    city: e.target.value
                  }
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-71" />
                </div>
                <div data-id="element-72">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-73">
                    Code postal *
                  </label>
                  <input type="text" required value={formData.address?.postalCode} onChange={e => setFormData({
                  ...formData,
                  address: {
                    ...formData.address!,
                    postalCode: e.target.value
                  }
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-74" />
                </div>
              </div>
            </div>
          </div>
          {/* Contact */}
          <div data-id="element-75">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-76">
              Contact
            </h3>
            <div className="space-y-4" data-id="element-77">
              <div data-id="element-78">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-79">
                  Nom du contact *
                </label>
                <input type="text" required value={formData.contact?.name} onChange={e => setFormData({
                ...formData,
                contact: {
                  ...formData.contact!,
                  name: e.target.value
                }
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-80" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-81">
                <div data-id="element-82">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-83">
                    Téléphone *
                  </label>
                  <input type="tel" required value={formData.contact?.phone} onChange={e => setFormData({
                  ...formData,
                  contact: {
                    ...formData.contact!,
                    phone: e.target.value
                  }
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-84" />
                </div>
                <div data-id="element-85">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-86">
                    Email *
                  </label>
                  <input type="email" required value={formData.contact?.email} onChange={e => setFormData({
                  ...formData,
                  contact: {
                    ...formData.contact!,
                    email: e.target.value
                  }
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-87" />
                </div>
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200" data-id="element-88">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-89">
              Annuler
            </button>
            <button type="submit" className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors" data-id="element-90">
              {client ? 'Enregistrer' : 'Créer le client'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}